import { useState } from "react";
import { PageHeader, Section } from "@/components/layout/Section";
import { MapPin, Mail, Phone, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function Contact() {
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const fd = new FormData(e.target);
    try {
      await api.post("/contact", {
        name: fd.get("name"),
        email: fd.get("email"),
        subject: fd.get("subject") || null,
        message: fd.get("message"),
      });
      toast.success("Message sent! We'll be in touch soon.");
      e.target.reset();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        kicker="Contact"
        title="We'd love to hear from you."
        subtitle="Have a question, prayer need, or want to plan a visit? Send us a message — we respond quickly."
      />

      <Section>
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-5">
            {[
              { icon: MapPin, t: "Address", d: "CLBC Main Sanctuary\nAccra, Ghana" },
              { icon: Mail, t: "Email", d: "hello@christlovebreedchurch.org" },
              { icon: Phone, t: "Phone", d: "+233 (0) 544 154 757" },
              { icon: Clock, t: "Office Hours", d: "Mon – Fri · 9:00 AM – 5:00 PM" },
            ].map((c, i) => (
              <div key={i} className="card-soft p-6 flex gap-4">
                <div className="w-11 h-11 rounded-full bg-gold/15 text-gold-500 flex items-center justify-center flex-shrink-0">
                  <c.icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-serif text-xl text-royal">{c.t}</div>
                  <div className="text-slate-600 text-sm mt-1 whitespace-pre-line">{c.d}</div>
                </div>
              </div>
            ))}

            <div className="rounded-2xl overflow-hidden card-soft h-64">
              <iframe
                title="CLBC location"
                data-testid="contact-map"
                src="https://www.google.com/maps?q=Accra%20Ghana&output=embed"
                className="w-full h-full border-0"
                loading="lazy"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} className="card-soft p-8 grid gap-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label>Full name</Label>
                  <Input name="name" required className="mt-2" data-testid="contact-name" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input name="email" type="email" required className="mt-2" data-testid="contact-email" />
                </div>
              </div>
              <div>
                <Label>Subject</Label>
                <Input name="subject" className="mt-2" data-testid="contact-subject" />
              </div>
              <div>
                <Label>Message</Label>
                <Textarea name="message" required rows={6} className="mt-2" data-testid="contact-message" />
              </div>
              <button disabled={busy} className="btn-primary w-fit" data-testid="contact-submit">
                Send Message
              </button>
            </form>
          </div>
        </div>
      </Section>
    </div>
  );
}
